import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Lock, ArrowRight } from 'lucide-react';
import { useUserQuery } from '@/api/v1/user-api';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from '@tanstack/react-router';

interface ProOnlyGuardProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

export function ProOnlyGuard({
  children,
  title = "Pro Feature",
  description = "This feature is available for Pro subscribers only.",
}: ProOnlyGuardProps) {
  const { data: user, isLoading } = useUserQuery();

  // Active subscription statuses (user still has access)
  const activeStatuses = ["active", "trialing", "past_due", "incomplete"];
  const isSubscriptionActive = user?.subscription_status
    ? activeStatuses.includes(user.subscription_status)
    : false;

  // Determine if user has Pro access
  const hasProAccess =
    user?.subscription_plan_slug === "pro" && isSubscriptionActive;

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
    );
  }

  if (!hasProAccess) {
    return (
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <Card className="w-full max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 p-4">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl flex items-center justify-center gap-2">
              <Crown className="h-6 w-6 text-primary" />
              {title}
            </CardTitle>
            <CardDescription className="text-base">{description}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="text-center">
              <Badge variant="outline" className="text-sm">
                Pro Plan Required
              </Badge>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-center">Upgrade to Pro to access:</h4>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-muted-foreground">
                  <div className="mr-3 h-1.5 w-1.5 rounded-full bg-primary" />
                  Advanced analytics & insights
                </li>
                <li className="flex items-center text-sm text-muted-foreground">
                  <div className="mr-3 h-1.5 w-1.5 rounded-full bg-primary" />
                  Unlimited geofences and flight plans
                </li>
                <li className="flex items-center text-sm text-muted-foreground">
                  <div className="mr-3 h-1.5 w-1.5 rounded-full bg-primary" />
                  Email notifications for geofence violations
                </li>
                <li className="flex items-center text-sm text-muted-foreground">
                  <div className="mr-3 h-1.5 w-1.5 rounded-full bg-primary" />
                  View drone remote ID detections from public sensors
                </li>
              </ul>
            </div>

            <div className="text-center pt-4">
              <Button asChild className="w-full sm:w-auto">
                <Link to="/subscriptions">
                  Upgrade to Pro
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
